import type { SeoIssue } from '@/types/api';
import type { StatusPresentation } from '@/utils/labels';

/**
 * SEO checks (ADR-0045) in the operator's words: what each issue means, with
 * what it is about, and how bad a site's state is at a glance.
 */

const TEXT: Record<string, (issue: SeoIssue) => string> = {
  unreachable: (issue) => `Главная не открывается: ${issue.detail ?? 'нет ответа'}`,
  noindex: (issue) => `Главная закрыта от индексации${issue.detail ? ` (${issue.detail})` : ''}`,
  robots_block: () => 'robots.txt закрывает сайт от поисковиков целиком',
  staging_open: () => 'Тестовый сайт открыт для индексации',
  canonical_foreign: (issue) => `canonical главной указывает на другой домен: ${issue.subject ?? '—'}`,
  canonical_missing: () => 'У главной нет canonical',
  title_missing: () => 'У главной нет title',
  title_length: (issue) => `Длина title главной — ${issue.detail} символов (рекомендуется 10–70)`,
  description_missing: () => 'У главной нет meta description',
  description_length: (issue) => `Длина description главной — ${issue.detail} символов (рекомендуется 50–170)`,
  h1_missing: () => 'На главной нет заголовка H1',
  h1_multiple: (issue) => `На главной ${issue.detail} заголовка H1`,
  lang_missing: () => 'Не указан язык страницы (<html lang>)',
  lang_mismatch: (issue) => `Язык страницы (${issue.subject}) не совпадает с языком сайта (${issue.detail})`,
  hreflang_invalid: (issue) => `Неверный код языка в hreflang: ${issue.subject}`,
  hreflang_self_missing: () => 'hreflang не содержит ссылку на сам сайт',
  hreflang_not_reciprocal: (issue) => `${issue.subject} не ссылается обратно через hreflang (${issue.detail})`,
  http_not_redirected: (issue) => `http:// не переводит на https://${issue.detail ? ` — ${issue.detail}` : ''}`,
  sitemap_missing: () => 'Sitemap не найден',
  sitemap_broken: (issue) => `Sitemap недоступен: ${issue.detail ?? ''}`,
  sitemap_lost: () => 'Sitemap перестал открываться',
  sitemap_foreign: (issue) => `В sitemap адреса на другом домене: ${issue.detail}`,
  sitemap_errors: (issue) => `Страницы из sitemap с ошибками: ${issue.detail}, например ${issue.subject}`,
  sitemap_shrunk: (issue) => `Число страниц в sitemap сократилось: ${issue.detail}`,
  key_page_error: (issue) => `Ключевая страница ${issue.subject} не открывается: ${issue.detail}`,
  key_page_noindex: (issue) => `Ключевая страница ${issue.subject} закрыта от индексации`,
  key_page_canonical: (issue) => `canonical ключевой страницы ${issue.subject} указывает на другой домен`,
  shared_verification: (issue) => `Код подтверждения ${issue.subject} совпадает с кодом ${issue.detail}`,
  shared_counter: (issue) => `Счётчик ${issue.subject} совпадает со счётчиком ${issue.detail}`,
  duplicate_title: (issue) => `title главной совпадает с ${issue.detail}`,
};

export function seoIssueText(issue: SeoIssue): string {
  return TEXT[issue.code]?.(issue) ?? issue.code;
}

export const SEO_LEVEL: Record<string, StatusPresentation> = {
  ok: { tone: 'healthy', label: 'В порядке', glyph: '●', hint: 'Замечаний нет' },
  info: { tone: 'healthy', label: 'Есть рекомендации', glyph: '●', hint: 'Только рекомендации' },
  warning: { tone: 'degraded', label: 'Есть замечания', glyph: '▲', hint: 'Есть проблемы, влияющие на поиск' },
  critical: { tone: 'down', label: 'Критично', glyph: '■', hint: 'Сайт недоступен для поиска' },
  none: { tone: 'pending', label: 'Не проверялся', glyph: '○', hint: 'Проверка ещё не выполнялась' },
};

export const SEO_SEVERITY: Record<string, StatusPresentation> = {
  critical: SEO_LEVEL['critical']!,
  warning: { tone: 'degraded', label: 'Замечание', glyph: '▲', hint: 'Влияет на поиск' },
  info: { tone: 'unknown', label: 'Рекомендация', glyph: 'i', hint: 'Не влияет на индексацию' },
};

/** What a field that changed is called in the site's trail. */
export const SEO_CHANGE_FIELDS: Record<string, string> = {
  indexing: 'индексация главной',
  robots: 'robots.txt',
  title: 'title главной',
  description: 'description главной',
  canonical: 'canonical главной',
  lang: 'язык страницы',
  sitemap: 'адрес sitemap',
  sitemap_urls: 'страниц в sitemap',
};

/** Google Search Console (ADR-0049): where a site's connection stands. */
export const SEARCH_STATE: Record<string, StatusPresentation> = {
  requested: { tone: 'pending', label: 'Подключается', glyph: '◌', hint: 'Панель получает токен Google и создаёт запись TXT' },
  awaiting_dns: { tone: 'maintenance', label: 'Нужна запись TXT', glyph: '◆', hint: 'Панель не может менять DNS этого домена: добавьте запись вручную' },
  verifying: { tone: 'pending', label: 'Подтверждение', glyph: '◌', hint: 'Google проверяет запись TXT; это может занять до нескольких часов' },
  verified: { tone: 'healthy', label: 'Подключён', glyph: '●', hint: 'Данные читаются раз в сутки' },
  failed: { tone: 'down', label: 'Не подключён', glyph: '■', hint: 'Подтверждение не прошло' },
  none: { tone: 'unknown', label: 'Не подключён', glyph: '○', hint: 'Сайт не подключён к Search Console' },
};

/** What Google says of a page's place in its index. */
export const INDEX_VERDICT: Record<string, StatusPresentation> = {
  PASS: { tone: 'healthy', label: 'В индексе', glyph: '●', hint: 'Страница в индексе Google' },
  PARTIAL: { tone: 'degraded', label: 'С замечаниями', glyph: '▲', hint: 'В индексе, но с проблемами' },
  NEUTRAL: { tone: 'degraded', label: 'Не в индексе', glyph: '▲', hint: 'Google знает страницу, но не индексирует её' },
  FAIL: { tone: 'down', label: 'Ошибка', glyph: '■', hint: 'Google не может проиндексировать страницу' },
  unknown: { tone: 'unknown', label: 'Нет данных', glyph: '○', hint: 'Google не ответил' },
};

/** A domain check's verdict (ADR-0050). */
export const DOMAIN_VERDICT: Record<string, StatusPresentation> = {
  clean: { tone: 'healthy', label: 'Чисто', glyph: '●', hint: 'Настораживающих признаков нет' },
  care: { tone: 'degraded', label: 'Проверить', glyph: '▲', hint: 'Есть признаки, на которые стоит посмотреть' },
  risk: { tone: 'down', label: 'Риск', glyph: '■', hint: 'Есть серьёзные признаки проблем' },
  none: { tone: 'pending', label: 'Нет данных', glyph: '○', hint: 'Источники ещё не прочитаны' },
};

const NOTE_TEXT: Record<string, (params: Readonly<Record<string, string | number>>) => string> = {
  no_history: () => 'В Web Archive нет снимков: домен новый или не использовался',
  history_gap: (params) => `Перерыв в истории ${params['from']}–${params['to']}: домен мог сменить владельца`,
  young: (params) => `Первые снимки в архиве — ${params['year']} год`,
  spam_high: (params) => `Высокий спам-скор: ${params['score']} из 100`,
  spam_raised: (params) => `Повышенный спам-скор: ${params['score']} из 100`,
  links_concentrated: (params) => `${Number(params['backlinks']).toLocaleString('ru-RU')} ссылок всего с ${params['referringDomains']} доменов`,
  links_broken: (params) => `${params['share']}% ссылок ведут на несуществующие страницы`,
  not_in_graph: () => 'Open PageRank не знает этот домен',
};

export function domainNoteText(note: { readonly code: string; readonly params: Readonly<Record<string, string | number>> }): string {
  return NOTE_TEXT[note.code]?.(note.params) ?? note.code;
}

/** The timeline's figures (ADR-0054), in the order the card offers them. */
export const TIMELINE_METRIC_LABELS: Record<string, string> = {
  clicks: 'Клики',
  impressions: 'Показы',
  position: 'Позиция',
  domain_rating: 'DR',
  referring_domains: 'Ссылающиеся домены',
  organic_traffic: 'Трафик',
  page_rank: 'PageRank',
  backlinks: 'Обратные ссылки',
};

export const TIMELINE_SOURCE_LABELS: Record<string, string> = {
  google: 'Search Console',
  ahrefs: 'Ahrefs',
  dataforseo: 'DataForSEO',
  openpagerank: 'Open PageRank',
  wayback: 'Web Archive',
};
